import errorToString from '../misc/errorToString.ts';
import { AccountMapping } from './accountMapping.ts';
import { Claim } from './claim.ts';
import { TaxMapping } from './taxMapping.ts';
import z, { ZodType } from 'zod';

declare global {
    // oxlint-disable-next-line no-shadow-restricted-names
    namespace globalThis {
        var config: UiConfig;
    }
}

export type Config = z.infer<typeof Config>;
export type UiConfig = z.infer<typeof UiConfig>;

const process = (globalThis as any).process;
const isDev = process?.env?.NODE_ENV === 'development';

const yaml = <T>(type: ZodType<T>) =>
    z.union([
        type,
        z
            .string()
            .transform((str, ctx) => {
                try {
                    return JSON.parse(str);
                } catch (error) {
                    ctx.issues.push({
                        code: 'invalid_format',
                        format: 'JSON',
                        input: str,
                        message: errorToString(error),
                    });
                    return z.NEVER;
                }
            })
            .pipe(type),
    ]);

export const Config = z.object({
    BUILD_BUILDNUMBER: z.string().default('local'),
    BUILDTIME: z.coerce.date().default(new Date()),

    // General config
    LOG_LEVEL: z.string().default('debug'),
    PORT: z.coerce.number().default(4001),
    WEB_BASE: z.string().default('/app/web'),
    USER_SYNC_SCHEDULE: z.string().default('*/5 * * * *'),
    USER_SYNC_FULL_SYNC_AFTER: z.coerce.number().default(24 * 60 * 60 * 1000),
    CLEAN_OLD_TMP_SCHEDULE: z.string().default('0 0 * * *'),
    MERGE_HISTORY_TRESHOLD: z.coerce.number().default(30 * 60 * 1000),
    BASE_URL: z.string(),
    TEMP_FOLDER: z.string().default(`.temp`),
    GOOGLE_MAPS_API_KEY: z.string().default('AIzaSyAIFvp1fWCFPArq4xkVATekIYsu9t9tf_c'),
    DEFAULT_NOTIFY_OWN_EMAIL: z.coerce.boolean().default(false),
    DEFAULT_NOTIFY_ROLE_EMAIL: z.coerce.boolean().default(false),
    DEFAULT_RECEIPT_COUNTRY_CODE: z.string().default('DE'),
    DEV: z.coerce.boolean().default(isDev),
    TIME_ZONE: z.string().default('Europe/Berlin'),
    ENCRYPTION_KEY: z.string().optional(),
    OPENAI_API_KEY: z.string().optional(),
    ALL_USERS_ADDITIONAL_CLAIMS: yaml(Claim.array()).default([]),

    // Company specifics
    COMPANY_NAME: z.string().default('Pentland Firth Software GmbH'),

    // Mongo connection
    MONGO_CONNECTION: z.string(),
    MONGO_DB: z.string(),
    MONGO_COL_BUCKETS: z.string().default('buckets'),
    MONGO_COL_BUCKETS_ARCHIVE: z.string().default('bucketsArchive'),
    MONGO_COL_USERS: z.string().default('users'),
    MONGO_COL_USER_SYNC_HISTORY: z.string().default('userSyncHistory'),
    MONGO_COL_CONFIG: z.string().default('config'),
    MONGO_COL_JOBS: z.string().default('jobs'),
    MONGO_COL_MIGRATIONS: z.string().default('migrations'),
    MONGO_COL_LOCKS: z.string().default('locks'),

    // User auth
    ACCESS_TOKEN_VALID: z.string().default('15 minutes'),
    ACCESS_TOKEN_SECRET: z.string().min(1),
    /** Enables the e2e test login endpoint (see e2eLoginPlugin). Never set in production. */
    E2E_TEST_LOGIN: z.coerce.boolean().default(false),

    // better-auth (login via Microsoft/Entra OAuth)
    BETTER_AUTH_SECRET: z.string().min(1),

    // MS Graph API + Microsoft/Entra login
    MSAL_AUTHORITY: z
        .string()
        .default('https://login.microsoftonline.com/ea3fdc0a-7310-4a7a-a09e-eadf62e7b875'),
    MSAL_CLIENT_ID: z.string(),
    MSAL_CLIENT_SECRET: z.string().optional(),
    MSAL_TENANT_ID: z.string().default('ea3fdc0a-7310-4a7a-a09e-eadf62e7b875'),
    APP_USER_PRINCIPAL_NAME: z.string(),
    /**
     * Base url of the MS Graph API used for sending emails. The Graph client appends the API
     * version (`v1.0`) and the resource path itself. Pointed at a mock in e2e tests.
     */
    GRAPH_BASE_URL: z.string().default('https://graph.microsoft.com'),
    /**
     * Static Graph access token that bypasses the MSAL client-credentials flow. Test-only
     * (mocked Graph API) - never set in production.
     */
    GRAPH_ACCESS_TOKEN: z.string().optional(),

    // Azure blob storage connection
    BLOB_CONNECTION_STRING: z.string(),
    BLOB_CONTAINER: z.string().default('receipts'),

    // General payment config
    PAYMENT_SERVICE: z.enum(['manual', 'odoo', 'qonto']).default('odoo'),
    PAYMENT_SCHEDULE: z.string().default('0 8 * * *'), // every day at 8am
    PAYMENT_UPDATE_SCHEDULE: z.string().default('*/5 * * * *'), // every 5 minutes

    // Qonto payment integration
    QONTO_ENABLED: z.coerce.boolean().default(false),
    QONTO_BASE_URL: z.string().optional(),
    QONTO_OAUTH_URL: z.string().optional(),
    QONTO_STAGING_TOKEN: z.string().optional(),
    QONTO_CLIENT_ID: z.string().optional(),
    QONTO_CLIENT_SECRET: z.string().optional(),
    QONTO_SCOPE: z
        .string()
        .or(z.array(z.string()))
        .transform((val) => (typeof val === 'string' ? val.split(',') : val))
        .default([
            'offline_access',
            'attachment.write',
            'request_transfers.write',
            'organization.read',
        ]),
    QONTO_ORGANIZATION_ID: z.string().optional(),

    // Odoo integration
    ODOO_BASE_URL: z.string().optional(),
    ODOO_TOKEN: z.string().optional(),
    ODOO_ACCOUNTS: yaml(z.array(AccountMapping)).default([]),
    ODOO_TAXES: yaml(z.array(TaxMapping)).default([]),
    ODOO_INVOICE_DUE_DAYS: z.coerce.number().int().nonnegative().default(14),
});

const requiredQontoConfigs = [
    'QONTO_ENABLED',
    'QONTO_BASE_URL',
    'QONTO_OAUTH_URL',
    'QONTO_CLIENT_ID',
    'QONTO_CLIENT_SECRET',
    'ENCRYPTION_KEY',
] as const;

export function isQontoConfigComplete(
    config: Config,
): config is Config & Required<Pick<Config, (typeof requiredQontoConfigs)[number]>> {
    const missingConfigs = requiredQontoConfigs.filter((key) => !config[key]);
    return missingConfigs.length === 0;
}

export function assertQontoConfigComplete(
    config: Config,
): asserts config is Config & Required<Pick<Config, (typeof requiredQontoConfigs)[number]>> {
    const missingConfigs = requiredQontoConfigs.filter((key) => !config[key]);

    if (missingConfigs.length > 0) {
        throw new Error(
            `Qonto configuration is missing the following keys: ${missingConfigs.join(', ')}`,
        );
    }
}

const requiredOdooConfigs = ['ODOO_BASE_URL', 'ODOO_TOKEN'] as const;
export function isOdooConfigComplete(config: Config): boolean {
    const missingConfigs = requiredOdooConfigs.filter((key) => !config[key]);
    return missingConfigs.length === 0;
}

export const UiConfig = Config.pick({
    BUILD_BUILDNUMBER: true,
    BUILDTIME: true,
    GOOGLE_MAPS_API_KEY: true,
    TIME_ZONE: true,
    COMPANY_NAME: true,
    PAYMENT_SERVICE: true,
    ALL_USERS_ADDITIONAL_CLAIMS: true,
});
